import type { ChatStatus, UIMessage } from 'ai';
import { useEffect } from 'react';

function toolIdentity(message: UIMessage): string | null {
  const calls: string[][] = [];
  for (const part of message.parts) {
    if (!part.type.startsWith('tool-')) continue;
    if (
      !('toolCallId' in part) ||
      typeof part.toolCallId !== 'string' ||
      !('state' in part) ||
      (part.state !== 'output-available' && part.state !== 'output-error')
    )
      return null;
    calls.push([part.type, part.toolCallId]);
  }
  return calls.length ? JSON.stringify(calls) : null;
}

function sameUserMessage(left: UIMessage, right: UIMessage): boolean {
  if (left.role !== 'user' || right.role !== 'user') return false;
  const content = (message: UIMessage) => {
    if (
      message.parts.some((part) => part.type !== 'text' && part.type !== 'file')
    )
      return null;
    return JSON.stringify(
      message.parts.map((part) =>
        part.type === 'text'
          ? { type: part.type, text: part.text }
          : part.type === 'file'
            ? { type: part.type, url: part.url, mediaType: part.mediaType }
            : null
      )
    );
  };
  const leftContent = content(left);
  return leftContent !== null && leftContent === content(right);
}

/** Merge saved turns only when they retain this view's full message order. */
export function appendPersistedChatMessages(
  current: UIMessage[],
  persisted: UIMessage[]
): UIMessage[] {
  if (persisted.length <= current.length) return current;

  const localById = new Map(current.map((message) => [message.id, message]));
  if (localById.size !== current.length) return current;
  const persistedIds = new Set(persisted.map((message) => message.id));
  if (persistedIds.size !== persisted.length) return current;

  // Live message IDs are replaced when the API saves the turn. Tool call IDs
  // survive that save, so they can prove a completed pair's identity. Never
  // match a bare repeated approval ("Yes, go ahead.") by text alone.
  const localForSavedId = new Map(localById);
  const savedIdForLocalId = new Map<string, string>();
  const savedToolIdentities = new Map<string, number[]>();
  for (const [index, message] of persisted.entries()) {
    if (message.role !== 'assistant') continue;
    const identity = toolIdentity(message);
    if (identity) {
      const indexes = savedToolIdentities.get(identity) ?? [];
      indexes.push(index);
      savedToolIdentities.set(identity, indexes);
    }
  }
  for (let index = 1; index < current.length; index++) {
    const assistant = current[index];
    const user = current[index - 1];
    if (
      assistant.role !== 'assistant' ||
      user.role !== 'user' ||
      persistedIds.has(assistant.id) ||
      persistedIds.has(user.id)
    )
      continue;
    const identity = toolIdentity(assistant);
    if (!identity) continue;
    const matches = savedToolIdentities.get(identity);
    if (matches?.length !== 1) continue;
    const savedIndex = matches[0];
    const savedAssistant = persisted[savedIndex];
    const savedUser = persisted[savedIndex - 1];
    if (
      !savedUser ||
      !sameUserMessage(user, savedUser) ||
      localForSavedId.has(savedUser.id) ||
      localForSavedId.has(savedAssistant.id)
    )
      continue;
    localForSavedId.set(savedUser.id, user);
    localForSavedId.set(savedAssistant.id, assistant);
    savedIdForLocalId.set(user.id, savedUser.id);
    savedIdForLocalId.set(assistant.id, savedAssistant.id);
  }

  const seen = new Set<string>();
  let nextLocal = 0;
  for (const message of persisted) {
    if (seen.has(message.id)) return current;
    seen.add(message.id);
    const localId = current[nextLocal]?.id;
    const savedId = localId ? savedIdForLocalId.get(localId) : undefined;
    if (message.id === (savedId ?? localId)) {
      if (message.role !== current[nextLocal]?.role) return current;
      nextLocal++;
    } else if (localForSavedId.has(message.id)) {
      // A reordered or conflicting snapshot is not a safe history extension.
      return current;
    }
  }
  // An unsaved optimistic message must not be dropped by a fetched snapshot.
  if (nextLocal !== current.length) return current;

  // Another tab may have saved turns between this view's completed turns.
  // Insert those in persisted order while retaining local card state/content.
  return persisted.map((message) => localForSavedId.get(message.id) ?? message);
}

/** useChat reads its initial messages once; a refreshed query needs a bridge. */
export function usePersistedChatMessages({
  messages,
  persistedMessages,
  status,
  setMessages,
}: {
  messages: UIMessage[];
  persistedMessages: UIMessage[];
  status: ChatStatus;
  setMessages: (
    next: UIMessage[] | ((current: UIMessage[]) => UIMessage[])
  ) => void;
}) {
  useEffect(() => {
    if (
      status !== 'ready' ||
      appendPersistedChatMessages(messages, persistedMessages) === messages
    )
      return;
    // Recheck the current SDK state inside the updater: a send may have added
    // an optimistic user message since this effect was scheduled.
    setMessages((current) =>
      appendPersistedChatMessages(current, persistedMessages)
    );
  }, [messages, persistedMessages, setMessages, status]);
}
